import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { ROLE_LABELS } from "@/lib/brand";
import { LogOut, Menu, X } from "lucide-react";

export function AppShell({ children, nav = [], dark = false }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-background grain">
      <header
        className="sticky top-0 z-40 border-b-2 border-ink bg-background/90 backdrop-blur"
        data-testid="app-header"
      >
        <div className="mx-auto max-w-6xl px-4 h-16 flex items-center justify-between gap-4">
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-2 press"
            data-testid="nav-home-logo"
          >
            <Logo type="wordmark" variant="black" height={30} />
          </button>

          <nav className="hidden md:flex items-center gap-1">
            {nav.map((item) => {
              const active = location.pathname === item.to || location.pathname.startsWith(item.to + "/");
              return (
                <button
                  key={item.to}
                  onClick={() => navigate(item.to)}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={`px-4 py-2 rounded-full text-sm font-semibold press border-2 ${
                    active
                      ? "bg-primary text-primary-foreground border-ink shadow-pop-sm"
                      : "bg-transparent border-transparent hover:border-ink"
                  }`}
                >
                  {item.label}
                </button>
              );
            })}
          </nav>

          <div className="flex items-center gap-3">
            <div className="hidden sm:flex flex-col items-end leading-tight">
              <span className="text-sm font-bold">{user?.name}</span>
              <span className="text-[11px] uppercase tracking-wide text-muted-foreground font-semibold">
                {ROLE_LABELS[user?.role] || user?.role}
              </span>
            </div>
            <button
              onClick={handleLogout}
              title="Sign out"
              data-testid="logout-button"
              className="h-10 w-10 grid place-items-center rounded-full border-2 border-ink bg-white press"
            >
              <LogOut size={16} />
            </button>
            {nav.length > 0 && (
              <button
                className="md:hidden h-10 w-10 grid place-items-center rounded-full border-2 border-ink bg-white"
                onClick={() => setOpen((v) => !v)}
                data-testid="mobile-menu-toggle"
              >
                {open ? <X size={18} /> : <Menu size={18} />}
              </button>
            )}
          </div>
        </div>

        {open && nav.length > 0 && (
          <div className="md:hidden border-t-2 border-ink bg-background px-4 py-3 flex flex-col gap-2">
            {nav.map((item) => (
              <button
                key={item.to}
                onClick={() => {
                  navigate(item.to);
                  setOpen(false);
                }}
                className="text-left px-4 py-2 rounded-xl font-semibold border-2 border-ink bg-white press"
              >
                {item.label}
              </button>
            ))}
          </div>
        )}
      </header>

      <main className="mx-auto max-w-6xl px-4 py-8">{children}</main>
    </div>
  );
}
